import { z } from "zod";
import { amsterdamDate, amsterdamParts } from "./amsterdam.js";

/**
 * Bezoek en aanwezigheid (docs/BEZOEK.md, issue #256). Eén bezoek loopt van aankondigen tot vertrek. Vooraf weten
 * wie er komt (toegang, begeleiding, bewoners informeren); tijdens het bezoek weten wie er binnen is (BHV,
 * veiligheid, ontruiming).
 */
export const VISIT_STATUSES = ["announced", "approved", "rejected", "inside", "left", "no_show", "cancelled"] as const;
export type VisitStatus = (typeof VISIT_STATUSES)[number];
export const VISIT_STATUS_LABELS: Record<VisitStatus, string> = {
  announced: "Te keuren",
  approved: "Verwacht",
  rejected: "Afgewezen",
  inside: "Binnen",
  left: "Vertrokken",
  no_show: "Niet gekomen",
  cancelled: "Afgezegd",
};
/** Bezoek dat nog kan komen of nu binnen is. */
export const OPEN_VISIT_STATUSES: readonly VisitStatus[] = ["announced", "approved", "inside"];

/** Wie er komt: een collega (eigen dienst), een persoon uit het adresboek of iemand die nog nergens staat. */
export const VISITOR_KINDS = ["member", "person", "guest"] as const;
export type VisitorKind = (typeof VISITOR_KINDS)[number];

/** Hoe het bezoek is ontstaan. */
export const VISIT_SOURCES = ["manual", "planning", "link", "walk_in", "self"] as const;
export type VisitSource = (typeof VISIT_SOURCES)[number];
export const VISIT_SOURCE_LABELS: Record<VisitSource, string> = {
  manual: "Aangekondigd",
  planning: "Uit Planning",
  link: "Via aanmeldlink",
  walk_in: "Binnengelopen",
  self: "Zelf ingeklokt",
};

/** Regels in het logboek van een bezoek. Alleen toevoegen, nooit wijzigen. */
export const VISIT_EVENT_KINDS = [
  "announced",
  "approved",
  "rejected",
  "checked_in",
  "checked_out",
  "room",
  "no_show",
  "cancelled",
  "confirmed",
  "edited",
] as const;
export type VisitEventKind = (typeof VISIT_EVENT_KINDS)[number];
export const VISIT_EVENT_LABELS: Record<VisitEventKind, string> = {
  announced: "Aangekondigd",
  approved: "Goedgekeurd",
  rejected: "Afgewezen",
  checked_in: "Binnen",
  checked_out: "Vertrokken",
  room: "Ruimte gewijzigd",
  no_show: "Niet gekomen",
  cancelled: "Afgezegd",
  confirmed: "Bevestigd door gastheer",
  edited: "Gewijzigd",
};

/** Bron van een in- of uitklokregel. */
export const VISIT_EVENT_SOURCES = ["host", "qr", "app", "link"] as const;
export type VisitEventSource = (typeof VISIT_EVENT_SOURCES)[number];

/** Ruimte kiezen bij inklokken: per gebouw uit, optioneel of verplicht. */
export const ROOM_MODES = ["off", "optional", "required"] as const;
export type RoomMode = (typeof ROOM_MODES)[number];
export const ROOM_MODE_LABELS: Record<RoomMode, string> = { off: "Uit", optional: "Optioneel", required: "Verplicht" };

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Gebruik uu:mm, bijvoorbeeld 19:00");
const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

/** Wie er komt. Een gast zonder adresboekregel heeft minstens een naam. */
export const visitorSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("member"), userId: z.uuid() }),
  z.object({ kind: z.literal("person"), personId: z.uuid() }),
  z.object({
    kind: z.literal("guest"),
    name: z.string().trim().min(1, "Vul een naam in").max(120),
    company: optText(120),
    email: z.email("Dit is geen geldig e-mailadres").max(200).nullable().optional().or(z.literal("")),
    phone: optText(40),
  }),
]);
export type VisitorInput = z.infer<typeof visitorSchema>;

export const visitCreateSchema = z
  .object({
    visitor: visitorSchema,
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }).nullable().optional(),
    purpose: optText(300),
    roomIds: z.array(z.uuid()).max(20).optional(),
    accessNote: optText(500),
    escortUserId: z.uuid().nullable().optional(),
    hostUserId: z.uuid().nullable().optional(),
  })
  .refine((v) => !v.endsAt || new Date(v.endsAt) > new Date(v.startsAt), {
    message: "Het einde ligt voor het begin",
    path: ["endsAt"],
  });
export type VisitCreate = z.infer<typeof visitCreateSchema>;

export const visitUpdateSchema = z.object({
  startsAt: z.iso.datetime({ offset: true }).optional(),
  endsAt: z.iso.datetime({ offset: true }).nullable().optional(),
  purpose: optText(300),
  roomIds: z.array(z.uuid()).max(20).optional(),
  accessNote: optText(500),
  escortUserId: z.uuid().nullable().optional(),
  hostUserId: z.uuid().nullable().optional(),
});
export type VisitUpdate = z.infer<typeof visitUpdateSchema>;

export const visitDecisionSchema = z.object({ note: optText(500) });
export const visitCheckInSchema = z.object({ roomId: z.uuid().nullable().optional() });
export const visitCheckOutSchema = z.object({
  /** Verplicht als iemand anders dan de bezoeker uitklokt (gastheer of receptie). */
  reason: z.string().trim().min(1, "Geef een reden").max(300),
});
export const visitRoomSchema = z.object({ roomId: z.uuid().nullable() });

export const visitSettingsSchema = z.object({
  hostUserIds: z.array(z.uuid()).max(50),
  accessNote: optText(1000),
  membersCheckIn: z.boolean(),
  walkIns: z.boolean(),
  roomMode: z.enum(ROOM_MODES),
  /** Wie na dit tijdstip nog binnen staat, staat bij Te controleren. */
  lateAfter: hhmm,
});
export type VisitSettingsInput = z.infer<typeof visitSettingsSchema>;

export const visitDayQuery = z.object({ dag: z.iso.date().optional() });

export interface VisitPartyDto {
  kind: VisitorKind;
  /** Lid of adresboekpersoon; null bij een gast. */
  userId: string | null;
  personId: string | null;
  companyId: string | null;
  name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
}

export interface VisitRoomDto {
  id: string;
  name: string;
  floor: string;
}

export interface VisitDto {
  id: string;
  buildingId: string;
  status: VisitStatus;
  source: VisitSource;
  visitor: VisitPartyDto;
  purpose: string | null;
  startsAt: string;
  endsAt: string | null;
  rooms: VisitRoomDto[];
  /** Laatst opgegeven ruimte tijdens het bezoek. */
  room: VisitRoomDto | null;
  accessNote: string | null;
  host: { id: string; name: string } | null;
  escort: { id: string; name: string } | null;
  checkedInAt: string | null;
  checkedOutAt: string | null;
  /** Binnengelopen en nog niet bevestigd door een gastheer. */
  needsConfirm: boolean;
  /** Nog binnen na het tijdstip van het gebouw, of verwacht en niet gekomen. */
  needsCheck: boolean;
  /** Mag de ingelogde gebruiker goedkeuren en bevestigen (gastheer of beheerder)? */
  canDecide: boolean;
}

export interface VisitEventDto {
  id: string;
  kind: VisitEventKind;
  source: VisitEventSource | null;
  note: string | null;
  by: string | null;
  at: string;
}

export interface VisitSettingsDto {
  hosts: { id: string; name: string }[];
  accessNote: string | null;
  membersCheckIn: boolean;
  walkIns: boolean;
  roomMode: RoomMode;
  lateAfter: string;
}

export interface VisitDayDto {
  day: string;
  visits: VisitDto[];
  settings: VisitSettingsDto;
}

export const DEFAULT_VISIT_SETTINGS: Omit<VisitSettingsDto, "hosts"> = {
  accessNote: null,
  membersCheckIn: true,
  walkIns: true,
  roomMode: "optional",
  lateAfter: "19:00",
};

/**
 * Te controleren: nog binnen op een eerdere dag of na het tijdstip van vandaag; of goedgekeurd/aangekondigd en
 * het einde (of de dag van het begin) is voorbij zonder dat iemand binnen kwam.
 */
export function visitNeedsCheck(
  v: { status: VisitStatus; startsAt: Date; endsAt: Date | null; checkedInAt: Date | null },
  lateAfter: string,
  now = new Date(),
): boolean {
  const today = amsterdamDate(now);
  if (v.status === "inside") {
    const inDay = v.checkedInAt ? amsterdamDate(v.checkedInAt) : today;
    if (inDay < today) return true;
    const p = amsterdamParts(now);
    const minutes = p.hour * 60 + p.minute;
    const [h, m] = lateAfter.split(":").map(Number) as [number, number];
    return minutes >= h * 60 + m;
  }
  if (v.status === "announced" || v.status === "approved") {
    if (v.endsAt) return v.endsAt.getTime() < now.getTime();
    return amsterdamDate(v.startsAt) < today;
  }
  return false;
}
